import { Alert, Pill, Text, Title } from "@mantine/core";
import { useEffect } from "react";
import { useAnlagenStromstgGenerators } from "../../../hooks/useAnlagenStromstgGenerators";
import { useGenerators } from "../../../hooks/useGenerators";
import { useVariantSites } from "../../../hooks/useVariantSites";
import { AlertPresets } from "../../../mantine/mantinePresets";
import { OptiSupportEndpoints } from "../../../urls";
import type { Generator } from "../../../utils/backend-types";
import { LoadOrError } from "../../LoadOrError/LoadOrError";
import { OptiSupportHelpLink } from "../../OptiSupportHelpLink/OptiSupportHelpLink";
import { getGeneratorNameWithSite } from "../utils/getGeneratorNameWithSite";
import "./AnlageStromstgFlow.scss";
import { AnlageStromstgForm } from "./AnlageStromstgForm/AnlageStromstgForm";
import { useAnlagenStromstgMutation } from "./hooks/useAnlagenStromstgMutation";

interface AnlageStromstgFlowProps {
  buttonContainer?: HTMLElement;
  currentGenerator: Generator;
  currentGeneratorIsNew?: boolean;
  multiSite?: boolean;
  shouldSkip?: boolean;
  siteId: number;
  variantId: number;
  onSubmit: () => void;
}

function AnlageStromstgFlow({
  buttonContainer,
  currentGenerator,
  currentGeneratorIsNew = false,
  multiSite,
  shouldSkip,
  siteId,
  variantId,
  onSubmit
}: AnlageStromstgFlowProps) {
  /*
    shouldSkip instantly calls onSubmit, which means this step is omitted.
    Since this step should currently only be shown for generators of type wind,
    its skipped for every other generator type.
  */

  const {
    sites,
    isLoading: isSitesLoading,
    error: sitesError
  } = useVariantSites(variantId);
  const {
    data: generators,
    isLoading: isGeneratorsLoading,
    error: generatorsError
  } = useGenerators({ personId: currentGenerator.person });
  const {
    anlagenStromstgGenerators,
    isLoading: isAnlagenStromstgGeneratorsLoading,
    error: anlagenStromstgGeneratorsError
  } = useAnlagenStromstgGenerators(currentGenerator.anlageStromstg);
  const anlagenStromstgMutation = useAnlagenStromstgMutation(
    currentGenerator.anlageStromstg,
    siteId
  );

  useEffect(() => {
    if (shouldSkip) {
      onSubmit();
    }
  }, [onSubmit, shouldSkip]);

  async function handleSubmitAnlageStromstgForm(generatorIds: Array<number>) {
    try {
      await anlagenStromstgMutation.mutateAsync(generatorIds);
    } catch (error) {
      return Promise.reject(error);
    }

    onSubmit();

    return Promise.resolve();
  }

  function handleSkipStep() {
    onSubmit();
  }

  const filteredGenerators = multiSite
    ? generators
    : generators?.filter((generator) => generator.site === siteId);
  const currentSite = sites?.find((site) => site.id === siteId);

  return (
    <div className="AnlageStromstgFlow">
      <LoadOrError
        error={sitesError || generatorsError || anlagenStromstgGeneratorsError}
        loading={
          isSitesLoading ||
          isGeneratorsLoading ||
          isAnlagenStromstgGeneratorsLoading
        }
      >
        {sites && filteredGenerators && anlagenStromstgGenerators && (
          <>
            <div className="header">
              <Title order={6}>Stromsteuerinformationen</Title>
              <OptiSupportHelpLink
                optiSupportEndpoint={
                  OptiSupportEndpoints.VerklammerungVonAnlagen
                }
                text="opti.node-Hilfe"
              />
            </div>
            {!currentGeneratorIsNew && (
              <Alert
                className="edit-mode-alert"
                color={AlertPresets.info.color}
                mb="md"
              >
                Beim Hinzufügen eines Erzeugers zu einer bestehenden
                stromsteuerlichen Anlage werden die Stromsteuer betreffende
                regulatorische Informationen (z. B. zu bestehenden
                Stromsteuer-Befreiungen) der stromsteuerlichen Anlage
                beibehalten bzw. übernommen. Sofern vorhanden, werden die
                entsprechenden Informationen auf dem Erzeuger nicht übernommen
                bzw. verworfen.
              </Alert>
            )}
            {multiSite && currentSite && (
              <>
                <Text fw={500}>Bestehende Liegenschaft</Text>
                <Pill.Group mb="md" mt="md">
                  <Pill radius={0}>{currentSite.name}</Pill>
                </Pill.Group>
              </>
            )}
            <Text fw={500}>Bestehende Einheit</Text>
            <Pill.Group mb="md" mt="md">
              <Pill radius={0}>
                {getGeneratorNameWithSite(
                  currentGenerator.name,
                  currentGenerator.site,
                  sites
                )}
              </Pill>
            </Pill.Group>
            <Text fw={500}>Weitere Einheiten</Text>
            <AnlageStromstgForm
              buttonContainer={buttonContainer}
              cancelButtonText={
                currentGeneratorIsNew ? "Überspringen" : "Abbrechen"
              }
              currentGeneratorId={currentGenerator.id}
              generators={filteredGenerators}
              initiallySelectedGenerators={anlagenStromstgGenerators}
              multiSite={multiSite}
              sites={sites}
              onSkip={handleSkipStep}
              onSubmit={handleSubmitAnlageStromstgForm}
            />
          </>
        )}
      </LoadOrError>
    </div>
  );
}

export { AnlageStromstgFlow, AnlageStromstgFlowProps };
